export const blogs = [
  {
    slug: 'how-to-choose-right-career-after-class-12',
    publishedAt: '2026-03-01',
    readingTimeMinutes: 6,
    seo: {
      title: 'How to Choose the Right Career After Class 12 | Nirvana Career Counselling',
      description: 'Confused about which career to pick after Class 12? This guide helps students in Bangalore, Ranchi, and Jamshedpur cut through the noise and make a decision that actually fits them.',
      keywords: 'career after class 12, career guidance class 12 students, how to choose career after 12th, career counselling bangalore, career options after 12th india, best career after class 12',
      canonicalUrl: 'https://nirvanacareercounselling.com/blog/how-to-choose-right-career-after-class-12',
      ogImage: 'https://nirvanacareercounselling.com/logo512.png',
    },
    card: {
      title: 'How to Choose the Right Career After Class 12',
      excerpt: 'Hundreds of options, everyone has an opinion, and the stakes feel high. Here is a simple way to make a decision that actually fits you.',
      category: 'Student Guidance',
      categoryColor: 'teal',
    },
    body: [
      {
        type: 'paragraph',
        text: 'Choosing a career after Class 12 is one of the most stressful decisions a student faces. Everyone around you seems to have an opinion. Your parents want one thing, your friends are doing something else, and teachers say something different. It is easy to feel lost.',
      },
      {
        type: 'paragraph',
        text: 'The good news is that this does not have to be a guessing game. There is a simple way to think through it.',
      },
      {
        type: 'heading',
        text: 'Start With What You Are Actually Good At',
      },
      {
        type: 'paragraph',
        text: 'Most students confuse what they enjoy doing with what they are good at. Both matter, but your strengths are more important when it comes to long-term career success. You can enjoy watching football, but that does not mean a career in sports science is right for you.',
      },
      {
        type: 'paragraph',
        text: 'Sit down and honestly list the subjects and activities you do well without much effort. These natural strengths are usually the best starting point for finding a career fit.',
      },
      {
        type: 'heading',
        text: 'Look at What the Market Actually Needs',
      },
      {
        type: 'paragraph',
        text: 'India is changing fast. Some careers that were very popular 15 years ago now have fewer job openings. New fields are growing quickly. Here are some areas that have solid demand right now:',
      },
      {
        type: 'list',
        style: 'unordered',
        items: [
          'Technology and software (especially in cities like Bangalore)',
          'Healthcare and allied health (doctors, physiotherapy, nutrition)',
          'Data analysis and business intelligence',
          'Government services (still one of the most stable paths)',
          'Finance and accounting (CA, CFA, financial planning)',
          'Skilled trades and vocational careers (hugely underrated)',
        ],
      },
      {
        type: 'heading',
        text: 'Do Not Just Follow the Crowd',
      },
      {
        type: 'paragraph',
        text: 'Every year, thousands of students choose Engineering or Medicine not because they want to, but because it feels like the "safe" choice. The result is that many of them spend three to four years studying something they dislike, and then spend years more trying to switch.',
      },
      {
        type: 'paragraph',
        text: 'If everyone in your school is going into one field, that is actually a reason to think more carefully. Crowded fields are more competitive and lower-paying on average. Niche paths where you have genuine interest can lead to better outcomes.',
      },
      {
        type: 'callout',
        variant: 'tip',
        text: 'A useful question to ask yourself: Would I read about this field in my free time? If the answer is yes, that is a good sign.',
      },
      {
        type: 'heading',
        text: 'How Psychometric Testing Helps',
      },
      {
        type: 'paragraph',
        text: 'A psychometric test measures your interests, aptitude, and personality in a structured way. It removes guesswork. Instead of relying only on gut feeling or advice from others, you get data about yourself.',
      },
      {
        type: 'paragraph',
        text: 'These tests are most useful when a trained career counsellor helps you interpret the results. The numbers alone do not tell the full story. Context and conversation matter.',
      },
      {
        type: 'heading',
        text: 'When Is the Right Time to Talk to a Career Counsellor?',
      },
      {
        type: 'paragraph',
        text: 'The best time is before you make a big decision, not after. If you are in Class 12 and still unsure about your direction, a single focused session with a counsellor can give you more clarity than months of searching the internet.',
      },
      {
        type: 'paragraph',
        text: 'At Nirvana Career Counselling, we work with students across Bangalore, Ranchi, and Jamshedpur. Sessions are fully online so you can attend from anywhere. The first session is free.',
      },
      {
        type: 'cta',
        heading: 'Not sure which career is right for you?',
        subtext: 'Book a free session with our career counsellor today. Available online for students in Bangalore, Ranchi, and Jamshedpur.',
        buttonText: 'Book Your Free Session',
      },
    ],
  },

  {
    slug: 'what-is-psychometric-testing-career-guide',
    publishedAt: '2026-03-05',
    readingTimeMinutes: 5,
    seo: {
      title: 'What Is Psychometric Testing and How It Helps Your Career | Nirvana',
      description: 'Wondering what a psychometric test actually measures? This simple guide explains how psychometric assessments work and why they are one of the most useful tools for career planning.',
      keywords: 'psychometric test career counselling, what is psychometric testing, psychometric assessment india, career aptitude test, psychometric test for students, career assessment test bangalore',
      canonicalUrl: 'https://nirvanacareercounselling.com/blog/what-is-psychometric-testing-career-guide',
      ogImage: 'https://nirvanacareercounselling.com/logo512.png',
    },
    card: {
      title: 'What Is Psychometric Testing and How It Helps Your Career',
      excerpt: 'You may have heard the term, but what does a psychometric test actually measure? Here is a plain-language guide to how these assessments work.',
      category: 'Career Tools',
      categoryColor: 'yellow',
    },
    body: [
      {
        type: 'paragraph',
        text: 'Psychometric testing is one of the most useful tools in career counselling, but most students have no idea what it involves. Some think it is a quiz. Others think it will "judge" them. Neither is true.',
      },
      {
        type: 'paragraph',
        text: 'This guide explains what these tests actually measure, how they work, and what you can do with the results.',
      },
      {
        type: 'heading',
        text: 'What Does a Psychometric Test Measure?',
      },
      {
        type: 'paragraph',
        text: 'A good psychometric assessment looks at three things:',
      },
      {
        type: 'list',
        style: 'unordered',
        items: [
          'Aptitude: your natural ability in areas like numerical reasoning, verbal skills, and logical thinking',
          'Interests: the types of activities and subjects you are drawn to',
          'Personality: how you tend to behave, communicate, and work with others',
        ],
      },
      {
        type: 'paragraph',
        text: 'Together, these three areas give a picture of which careers are likely to be a good match for you. Not based on opinion, but based on data.',
      },
      {
        type: 'heading',
        text: 'Are Psychometric Tests Accurate?',
      },
      {
        type: 'paragraph',
        text: 'No test is 100 percent accurate for every person. But well-designed psychometric assessments have been validated across large groups and are significantly more reliable than choosing a career based on peer pressure or family tradition.',
      },
      {
        type: 'paragraph',
        text: 'The test itself is just one piece. The most important part is the conversation that happens after, where a counsellor helps you understand what the results mean for your specific situation.',
      },
      {
        type: 'callout',
        variant: 'highlight',
        text: 'Psychometric tests do not tell you what you must do. They show you where your strengths and interests align, so you can make a more informed choice.',
      },
      {
        type: 'heading',
        text: 'Who Should Take a Psychometric Test?',
      },
      {
        type: 'paragraph',
        text: 'These tests are useful for:',
      },
      {
        type: 'list',
        style: 'unordered',
        items: [
          'Students in Class 8 to 12 choosing subjects or streams',
          'Graduates deciding between job offers or further study',
          'Professionals considering a career change',
          'Parents who want an objective view of their child\'s strengths',
        ],
      },
      {
        type: 'heading',
        text: 'What Happens After the Test?',
      },
      {
        type: 'paragraph',
        text: 'After you complete the assessment, a detailed report is generated. This report covers your interest areas, aptitude scores, and personality profile. A career counsellor then walks you through the results and maps them to real career options.',
      },
      {
        type: 'paragraph',
        text: 'You come out of the session with a much clearer picture of what careers might suit you and a practical next step.',
      },
      {
        type: 'heading',
        text: 'How to Get a Psychometric Assessment',
      },
      {
        type: 'paragraph',
        text: 'At Nirvana Career Counselling, we offer psychometric assessments for students from Class 2 all the way through working professionals. All assessments are done online, so you can complete them from Bangalore, Jamshedpur, Ranchi, or anywhere else.',
      },
      {
        type: 'cta',
        heading: 'Find out what your strengths say about your career',
        subtext: 'First session is free. Online sessions available across India.',
        buttonText: 'Book Your Free Session',
      },
    ],
  },

  {
    slug: 'career-counselling-students-jamshedpur',
    publishedAt: '2026-03-08',
    readingTimeMinutes: 6,
    seo: {
      title: 'Career Counselling for Students in Jamshedpur | Nirvana Career Guidance',
      description: 'A practical guide to career counselling for students in Jamshedpur. Learn about career options in the Steel City, how online sessions work, and how to get started with a free consultation.',
      keywords: 'career counselling jamshedpur, career guidance jamshedpur students, career counsellor tata nagar, career options jamshedpur, online career counselling jamshedpur, career after class 10 jamshedpur',
      canonicalUrl: 'https://nirvanacareercounselling.com/blog/career-counselling-students-jamshedpur',
      ogImage: 'https://nirvanacareercounselling.com/logo512.png',
    },
    card: {
      title: 'Career Counselling for Students in Jamshedpur: What You Need to Know',
      excerpt: 'Jamshedpur has more career opportunities than most students realise. Here is a guide to making sense of your options in the Steel City.',
      category: 'Jamshedpur',
      categoryColor: 'teal',
    },
    body: [
      {
        type: 'paragraph',
        text: 'Jamshedpur is a unique city. It is home to some of India\'s biggest industrial companies, well-regarded schools, and a community that values hard work. But when it comes to career choices, students here often face the same problem: too many options, not enough guidance.',
      },
      {
        type: 'paragraph',
        text: 'This guide is written specifically for students and parents in Jamshedpur who want to make smarter career decisions.',
      },
      {
        type: 'heading',
        text: 'The Career Landscape in Jamshedpur',
      },
      {
        type: 'paragraph',
        text: 'Jamshedpur is best known for Tata Steel and the broader Tata Group. This shapes a lot of how people in the city think about careers. Engineering, metallurgy, and industrial management are respected paths here.',
      },
      {
        type: 'paragraph',
        text: 'But the city offers much more than that. Here is a look at the main career areas Jamshedpur students explore:',
      },
      {
        type: 'list',
        style: 'unordered',
        items: [
          'Industrial and manufacturing careers (engineering, quality control, operations)',
          'Government services (UPSC, JPSC, banking, railways)',
          'Healthcare (XLRI and nearby colleges have strong commerce and management programs)',
          'Information technology (remote work has made IT accessible from Jamshedpur)',
          'Business and entrepreneurship (the city has a growing small business community)',
          'Education and training',
        ],
      },
      {
        type: 'heading',
        text: 'What Jamshedpur Students Struggle With Most',
      },
      {
        type: 'paragraph',
        text: 'The most common issue we see is students choosing Science after Class 10 because it feels like the "right" thing to do in a city known for industry. Many then struggle with the subjects and end up either switching or sticking with something they dislike.',
      },
      {
        type: 'paragraph',
        text: 'The second most common issue is not knowing about options outside the city. Many students assume they need to move to Bangalore or Delhi to have a good career. That is often not true anymore, especially with remote work becoming normal.',
      },
      {
        type: 'callout',
        variant: 'tip',
        text: 'If you are in Class 9 or 10 in Jamshedpur, this is the best time to do a psychometric assessment. It gives you clear information before you pick your stream.',
      },
      {
        type: 'heading',
        text: 'How Online Career Counselling Works for Jamshedpur Students',
      },
      {
        type: 'paragraph',
        text: 'Online counselling is just as effective as meeting in person. Sessions are held over video call. You can attend from home, from school, or anywhere with a decent internet connection.',
      },
      {
        type: 'paragraph',
        text: 'Here is what a typical session looks like:',
      },
      {
        type: 'list',
        style: 'ordered',
        items: [
          'A free first session to understand your current situation and goals',
          'A psychometric assessment (completed online at your own pace)',
          'A follow-up session to go through results and map them to career options',
          'A personalised career roadmap with next steps',
        ],
      },
      {
        type: 'heading',
        text: 'Careers Beyond Tata Steel: What the Future Looks Like',
      },
      {
        type: 'paragraph',
        text: 'Tata Steel and related companies are important employers, but they are not the only path. Industries growing in and around Jamshedpur include logistics, retail, healthcare, and technology services. Students who explore these areas early are finding good opportunities.',
      },
      {
        type: 'paragraph',
        text: 'Nationally, the fastest-growing careers are in technology, data, and healthcare. A student in Jamshedpur today has the same access to these careers as someone in Bangalore, provided they have the right guidance and preparation.',
      },
      {
        type: 'cta',
        heading: 'Get career guidance designed for Jamshedpur students',
        subtext: 'Online sessions available. First consultation is completely free.',
        buttonText: 'Book Your Free Session',
      },
    ],
  },

  {
    slug: 'engineering-vs-management-career-decision-guide',
    publishedAt: '2026-03-12',
    readingTimeMinutes: 7,
    seo: {
      title: 'Engineering vs Management: How to Decide What Is Right for You | Nirvana',
      description: 'Torn between Engineering and an MBA? This straightforward guide helps students and graduates in India understand the real difference and figure out which path fits their goals.',
      keywords: 'engineering vs management career, should i do MBA or engineering, career decision engineering management, MBA vs engineering india, career counselling bangalore professionals, career guidance graduates',
      canonicalUrl: 'https://nirvanacareercounselling.com/blog/engineering-vs-management-career-decision-guide',
      ogImage: 'https://nirvanacareercounselling.com/logo512.png',
    },
    card: {
      title: 'Engineering or Management: How to Decide What Is Right for You',
      excerpt: 'Should you go into Engineering or pursue Management? This is one of the most common questions we get. Here is a clear way to think about it.',
      category: 'Career Decisions',
      categoryColor: 'yellow',
    },
    body: [
      {
        type: 'paragraph',
        text: '"Should I do Engineering or go for an MBA?" is one of the most common questions students and young professionals ask us. It comes up in Bangalore, Jamshedpur, Ranchi, and every other city we work in.',
      },
      {
        type: 'paragraph',
        text: 'There is no single right answer. The right choice depends on who you are and what you actually want from your career.',
      },
      {
        type: 'heading',
        text: 'What Engineering Actually Involves',
      },
      {
        type: 'paragraph',
        text: 'Engineering is a technical field. It requires strong analytical skills and comfort with problem-solving at a detailed level. The early years are usually spent working on specific technical tasks, building expertise in one area.',
      },
      {
        type: 'paragraph',
        text: 'Engineers who do well tend to enjoy:',
      },
      {
        type: 'list',
        style: 'unordered',
        items: [
          'Working through complex problems step by step',
          'Building, making, or fixing things (physical or digital)',
          'Developing deep expertise in a specific area over time',
          'Technical precision and accuracy',
        ],
      },
      {
        type: 'heading',
        text: 'What Management Actually Involves',
      },
      {
        type: 'paragraph',
        text: 'Management and MBA programs are about leading people, making decisions with incomplete information, and understanding how organisations work. The focus is broader and more people-oriented.',
      },
      {
        type: 'paragraph',
        text: 'People who do well in management tend to:',
      },
      {
        type: 'list',
        style: 'unordered',
        items: [
          'Enjoy working with and through people',
          'Like seeing the big picture rather than the fine details',
          'Be comfortable with ambiguity and frequent change',
          'Enjoy negotiating, persuading, and leading',
        ],
      },
      {
        type: 'callout',
        variant: 'highlight',
        text: 'Many engineers eventually move into management roles. The question is whether you want to start there or build technical depth first.',
      },
      {
        type: 'heading',
        text: 'The Most Common Mistake',
      },
      {
        type: 'paragraph',
        text: 'The biggest mistake students make is choosing based on salary expectations alone. Yes, IIM graduates and senior engineers earn well. But getting there requires years of genuine effort in a field you need to actually care about.',
      },
      {
        type: 'paragraph',
        text: 'Choosing Management because "engineering seems hard" or choosing Engineering because "MBA is expensive" are both bad reasons. Start with understanding your own strengths and interests.',
      },
      {
        type: 'heading',
        text: 'What If You Are Already an Engineer Considering an MBA?',
      },
      {
        type: 'paragraph',
        text: 'This is very common in Bangalore especially. Many IT professionals work for 3 to 5 years and then wonder whether to move into management or stay technical.',
      },
      {
        type: 'paragraph',
        text: 'A few questions worth asking honestly:',
      },
      {
        type: 'list',
        style: 'unordered',
        items: [
          'Do you find the technical work energising, or does it feel draining?',
          'Do you get more satisfaction from solving code problems or from coordinating people?',
          'Are you pursuing an MBA for the right reasons, or just because colleagues are doing it?',
        ],
      },
      {
        type: 'heading',
        text: 'How a Career Counsellor Can Help',
      },
      {
        type: 'paragraph',
        text: 'A psychometric assessment followed by a career counselling session can cut through this confusion quickly. Instead of spending months going back and forth, you get a data-backed view of which direction fits your actual profile.',
      },
      {
        type: 'paragraph',
        text: 'We work with graduates and professionals across Bangalore, Ranchi, and Jamshedpur. Sessions are online and the first one is free.',
      },
      {
        type: 'cta',
        heading: 'Still deciding between Engineering and Management?',
        subtext: 'A single session can give you more clarity than months of searching. First session is free.',
        buttonText: 'Book Your Free Session',
      },
    ],
  },

  {
    slug: 'career-change-professionals-bangalore',
    publishedAt: '2026-03-15',
    readingTimeMinutes: 6,
    seo: {
      title: 'Career Change After 30 in Bangalore: A Practical Guide | Nirvana',
      description: 'Thinking about switching careers in Bangalore? This guide covers how working professionals can plan a career change without starting from zero, including how a career counsellor can help.',
      keywords: 'career change bangalore, career switch professionals bangalore, career counselling professionals bangalore, how to change career in 30s, career transition bangalore IT, career counselling working professionals',
      canonicalUrl: 'https://nirvanacareercounselling.com/blog/career-change-professionals-bangalore',
      ogImage: 'https://nirvanacareercounselling.com/logo512.png',
    },
    card: {
      title: 'Career Change After 30 in Bangalore: A Practical Guide for Professionals',
      excerpt: 'Many professionals in Bangalore reach a point where their current career no longer feels right. Here is how to think through a change without panic.',
      category: 'Professionals',
      categoryColor: 'teal',
    },
    body: [
      {
        type: 'paragraph',
        text: 'Bangalore is full of people who built successful careers in IT or finance and then reached a point where they no longer felt satisfied. Some want more meaningful work. Others feel stuck in a role that does not use their best skills. A few simply realised they chose the wrong field to begin with.',
      },
      {
        type: 'paragraph',
        text: 'Whatever the reason, a career change in your 30s is not as risky as it feels. But it does need a plan.',
      },
      {
        type: 'heading',
        text: 'Why Career Changes Feel So Scary',
      },
      {
        type: 'paragraph',
        text: 'The fear usually comes from three places: financial pressure, sunk cost thinking ("I have already spent 10 years in this field"), and uncertainty about what to do next.',
      },
      {
        type: 'paragraph',
        text: 'The sunk cost issue is the biggest trap. Just because you have spent years in a field does not mean you should stay in it. The real question is: what gives you the best chance of a good career over the next 20 to 30 years?',
      },
      {
        type: 'heading',
        text: 'What You Are Not Starting From Zero',
      },
      {
        type: 'paragraph',
        text: 'Here is something most people miss. When you change careers at 30, you are not starting from zero. You bring:',
      },
      {
        type: 'list',
        style: 'unordered',
        items: [
          'Workplace experience (communication, deadlines, team dynamics)',
          'Industry knowledge that transfers to new fields',
          'A professional network',
          'Personal maturity and self-awareness',
          'Clarity about what you do and do not want',
        ],
      },
      {
        type: 'paragraph',
        text: 'A 22-year-old starting fresh does not have most of these. They are real advantages.',
      },
      {
        type: 'callout',
        variant: 'tip',
        text: 'Many professionals who switch careers in their 30s end up more successful than they would have been staying in their original field. The change forces them to be intentional.',
      },
      {
        type: 'heading',
        text: 'Popular Career Transitions in Bangalore',
      },
      {
        type: 'paragraph',
        text: 'Based on clients we work with in Bangalore, these are some of the most common and successful transitions:',
      },
      {
        type: 'list',
        style: 'unordered',
        items: [
          'Software engineer to product management',
          'IT professional to data science or AI roles',
          'Corporate professional to entrepreneur or consultant',
          'Operations or HR roles transitioning to training and coaching',
          'Finance professional moving into fintech or startup roles',
        ],
      },
      {
        type: 'heading',
        text: 'Steps to Plan a Career Change Without Regret',
      },
      {
        type: 'list',
        style: 'ordered',
        items: [
          'Get honest about why you want to change. Boredom alone is not enough of a reason. Understand the root cause.',
          'Take a psychometric assessment to identify your strongest aptitudes and interests.',
          'Research your target field properly. Talk to people who are already doing the work you want to do.',
          'Start building skills in the new field before you leave your current job where possible.',
          'Make a financial plan. Know how many months of savings you need to make the transition comfortable.',
          'Get a career counsellor to help you build and sense-check your transition plan.',
        ],
      },
      {
        type: 'heading',
        text: 'When Is the Right Time to Start?',
      },
      {
        type: 'paragraph',
        text: 'The right time to start thinking about a career change is before you are desperate. If you wait until you are burned out or already unhappy for two or three years, you make decisions under pressure.',
      },
      {
        type: 'paragraph',
        text: 'If you are a professional in Bangalore who is thinking about a change, even if it is just a vague feeling right now, a single career counselling session can help you figure out whether and how to act on it.',
      },
      {
        type: 'cta',
        heading: 'Thinking about a career change in Bangalore?',
        subtext: 'Talk to our career counsellor. One session can bring a lot of clarity. First session is free and fully online.',
        buttonText: 'Book Your Free Session',
      },
    ],
  },

  {
    slug: 'career-guidance-ranchi-opportunities',
    publishedAt: '2026-03-18',
    readingTimeMinutes: 5,
    seo: {
      title: 'Career Guidance in Ranchi: Opportunities Students and Parents Should Know | Nirvana',
      description: 'A practical career guidance resource for students and families in Ranchi. Covers the best career options in Jharkhand, common mistakes to avoid, and how online counselling works.',
      keywords: 'career guidance ranchi, career counselling ranchi, career options ranchi jharkhand, career counsellor ranchi, best careers ranchi, career after class 10 ranchi jharkhand, online career counselling ranchi',
      canonicalUrl: 'https://nirvanacareercounselling.com/blog/career-guidance-ranchi-opportunities',
      ogImage: 'https://nirvanacareercounselling.com/logo512.png',
    },
    card: {
      title: 'Career Guidance in Ranchi: What Students and Parents Should Know',
      excerpt: 'Ranchi has more career paths than most students are told about. This guide covers what is actually available and how to plan for it.',
      category: 'Ranchi',
      categoryColor: 'yellow',
    },
    body: [
      {
        type: 'paragraph',
        text: 'Ranchi is a growing city with a range of career opportunities that do not always get talked about. As the capital of Jharkhand, it has a mix of government, industrial, educational, and emerging private sector opportunities.',
      },
      {
        type: 'paragraph',
        text: 'Yet many students in Ranchi feel like they have only two real options: government jobs or moving to another city. That is not accurate, and this guide explains why.',
      },
      {
        type: 'heading',
        text: 'Career Paths That Are Genuinely Strong in Ranchi',
      },
      {
        type: 'paragraph',
        text: 'Here are the career areas where students from Ranchi consistently find good opportunities:',
      },
      {
        type: 'list',
        style: 'unordered',
        items: [
          'Government services: UPSC, JPSC, SSC, banking, and railways are all very accessible from Ranchi with the right preparation',
          'Healthcare: Ranchi has several reputed medical colleges and a growing demand for doctors, nurses, and allied health professionals',
          'Education: teaching at school and college level, coaching institutes',
          'IT and software: remote work means Ranchi students can work for companies anywhere in India',
          'Business and trade: Ranchi\'s growing economy creates real opportunities in retail, logistics, and local services',
          'Social sector: NGOs and development organisations are active in Jharkhand and offer meaningful careers',
        ],
      },
      {
        type: 'heading',
        text: 'The Biggest Career Planning Mistake in Ranchi',
      },
      {
        type: 'paragraph',
        text: 'The most common mistake we see among Ranchi students is choosing a stream or course based on what is prestigious in the community rather than what fits the individual student.',
      },
      {
        type: 'paragraph',
        text: 'Science stream is seen as high-status. As a result, many students who are not naturally suited to it end up struggling, and some fail to convert that into a useful career. There is no shame in Commerce or Arts. These streams lead to respected, well-paying careers.',
      },
      {
        type: 'callout',
        variant: 'highlight',
        text: 'A student who genuinely enjoys Commerce and becomes a CA will have a better career than a student who struggled through Science and ended up in a job they dislike.',
      },
      {
        type: 'heading',
        text: 'How Online Career Counselling Works for Ranchi Families',
      },
      {
        type: 'paragraph',
        text: 'Many families in Ranchi are not aware that high-quality career counselling is available online. You do not need to travel to Bangalore or Delhi for this.',
      },
      {
        type: 'paragraph',
        text: 'Our sessions are conducted over video call. Students take their psychometric assessment from home, and the counsellor goes through the results in a detailed follow-up session. The whole process can be done without leaving Ranchi.',
      },
      {
        type: 'heading',
        text: 'When Should Ranchi Students Seek Career Guidance?',
      },
      {
        type: 'paragraph',
        text: 'The most impactful times are:',
      },
      {
        type: 'list',
        style: 'unordered',
        items: [
          'Before Class 11 begins, when stream selection happens',
          'After Class 12 results, when college and course decisions need to be made',
          'During graduation, when job versus further study decisions arise',
          'When a student is struggling or losing motivation at any stage',
        ],
      },
      {
        type: 'paragraph',
        text: 'Parents often wait too long before seeking guidance. By the time a student is in the second year of the wrong course, the options to correct course are fewer. Early guidance is almost always more useful.',
      },
      {
        type: 'heading',
        text: 'What Parents Can Do Right Now',
      },
      {
        type: 'paragraph',
        text: 'If your child is in Class 9 or above and has not had a structured career counselling conversation, it is worth arranging one. Ask them what they actually enjoy, not what they think they should enjoy. And book a session so they can get a professional assessment done.',
      },
      {
        type: 'cta',
        heading: 'Career guidance for Ranchi students and families',
        subtext: 'Fully online sessions. No travel required. First session is free.',
        buttonText: 'Book Your Free Session',
      },
    ],
  },
];

export const getBlogBySlug = (slug) => blogs.find((b) => b.slug === slug);
